import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminClientEngagementsService } from '../services/admin.clientEngagements.service'
import { adminCompanyService } from '../services/admin.company.service'
import { adminEquipmentService } from '../services/admin.equipment.service'
import type { ClientEngagementDocument, CompanyMemberDocument, EquipmentDocument, EngagementStatus } from '@/types'
import type { EngagementFormValues } from '../types'

type Engagement = ClientEngagementDocument & { id: string }
type Member = CompanyMemberDocument & { id: string }
type Equipment = EquipmentDocument & { id: string }

export function useAdminClientEngagements() {
  const [engagements, setEngagements] = useState<Engagement[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [equipment, setEquipment] = useState<Equipment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<EngagementStatus | ''>('')
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  // Load engagements whenever filter or tick changes
  useEffect(() => {
    setLoading(true)
    setError(null)
    adminClientEngagementsService
      .listEngagements(statusFilter || undefined)
      .then(setEngagements)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load engagements.'))
      )
      .finally(() => setLoading(false))
  }, [statusFilter, tick])

  // Load members + equipment once (reference data for form dropdowns)
  useEffect(() => {
    Promise.all([
      adminCompanyService.getAllMembers(),
      adminEquipmentService.getAllEquipment(),
    ])
      .then(([m, e]) => {
        setMembers(m)
        setEquipment(e)
      })
      .catch(() => {
        // Non-critical — form dropdowns may be empty
      })
  }, [])

  const createEngagement = useCallback(
    async (data: EngagementFormValues): Promise<string> => {
      // Denormalize member names + equipment names
      const assignedMemberNames = data.assignedMemberIds
        .map((mid) => members.find((m) => m.id === mid)?.name)
        .filter((n): n is string => !!n)
      const equipItems = equipment.filter((e) => data.equipmentIds.includes(e.id))
      const enriched: EngagementFormValues = { ...data }
      const extras: { assignedMemberNames?: string[]; equipmentNames?: string[] } = {}
      if (assignedMemberNames.length > 0) extras.assignedMemberNames = assignedMemberNames
      if (equipItems.length > 0) extras.equipmentNames = equipItems.map((e) => e.name)
      const id = await adminClientEngagementsService.createEngagement(enriched)
      if (Object.keys(extras).length > 0) {
        await adminClientEngagementsService.updateEngagement(id, extras)
      }
      refresh()
      return id
    },
    [members, equipment, refresh]
  )

  const updateEngagement = useCallback(
    async (
      id: string,
      data: Partial<EngagementFormValues>
    ): Promise<void> => {
      const extras: { assignedMemberNames?: string[]; equipmentNames?: string[] } = {}
      if (data.assignedMemberIds !== undefined) {
        extras.assignedMemberNames = data.assignedMemberIds
          .map((mid) => members.find((m) => m.id === mid)?.name)
          .filter((n): n is string => !!n)
      }
      if (data.equipmentIds !== undefined) {
        const equipItems = equipment.filter((e) => data.equipmentIds!.includes(e.id))
        extras.equipmentNames = equipItems.map((e) => e.name)
      }
      await adminClientEngagementsService.updateEngagement(id, { ...data, ...extras })
      refresh()
    },
    [members, equipment, refresh]
  )

  const deleteEngagement = useCallback(
    async (id: string): Promise<void> => {
      await adminClientEngagementsService.deleteEngagement(id)
      refresh()
    },
    [refresh]
  )

  const lookupUserByEmail = useCallback(
    (email: string) => adminClientEngagementsService.lookupUserByEmail(email),
    []
  )

  const createClientUser = useCallback(
    (email: string, displayName: string) =>
      adminClientEngagementsService.createClientUser(email, displayName),
    []
  )

  return {
    engagements,
    members,
    equipment,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    createEngagement,
    updateEngagement,
    deleteEngagement,
    lookupUserByEmail,
    createClientUser,
    refresh,
  }
}
